import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import axios from 'axios';
import { API } from '@/App';
import usePageContent from '@/hooks/usePageContent';

const BlogListPage = () => {
  const { t: c } = usePageContent('blog');
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Ratgeber & Blog | Führerschein Tipps | Fahrschule Rößner';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', 'Nützliche Tipps und Ratgeber rund um den Führerschein. Kosten, Dauer, Theorieprüfung und mehr bei Fahrschule Rößner.');
    }

    axios.get(`${API}/blog`)
      .then(res => setArticles(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="pt-20" data-testid="blog-list-page">
      {/* Hero */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {c('hero_title', 'Ratgeber & Tipps')}
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            {c('hero_subtitle', 'Alles was du wissen musst rund um deinen Führerschein – von Kosten bis Prüfungstipps.')}
          </p>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <Link 
                key={article.slug} 
                to={`/blog/${article.slug}`}
                className="blog-card group"
                data-testid={`blog-article-${index}`}
              >
                <Card className="overflow-hidden h-full border-slate-200 hover:border-blue-200 hover:shadow-xl transition-all duration-300">
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={article.image_url} 
                      alt={article.title}
                      className="blog-card-image w-full h-full object-cover"
                    />
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(article.created_at).toLocaleDateString('de-DE', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        })}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        5 Min. Lesezeit
                      </div>
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-slate-600 text-sm line-clamp-3 mb-4">{article.excerpt}</p>
                    <div className="flex items-center text-blue-600 font-medium text-sm group-hover:translate-x-1 transition-transform">
                      Weiterlesen <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {articles.length === 0 && (
            <div className="text-center py-12">
              <p className="text-slate-600">Noch keine Artikel vorhanden.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogListPage;
